import { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { GameState, initGameState, Element } from './store/types'
import { apiUserLogout, apiCharLoad, apiTurnStatus, apiActionTick } from './server/ServiceCall'
import LoginPage from './pages/LoginPage'
import TabInven  from './pages/TabInven'
import TabMugong from './pages/TabMugong'
import TabTrain  from './pages/TabTrain'
import TabHunt   from './pages/TabHunt'
import TabShop   from './pages/TabShop'
import TabTrade  from './pages/TabTrade'
import TabMove   from './pages/TabMove'
import TabSect   from './pages/TabSect'
import TabRank   from './pages/TabRank'
import './css/App.css'

interface Session { token: string; userCode: number; userName: string }
const SESSION_KEY = 'mwl_session'
function loadSession(): Session | null {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null') } catch { return null }
}
function saveSession(s: Session) { localStorage.setItem(SESSION_KEY, JSON.stringify(s)) }
function clearSession()           { localStorage.removeItem(SESSION_KEY) }

function ServerDownOverlay() {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:99999,
      background:'rgba(0,0,0,0.92)',
      display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center', gap:20,
    }}>
      <div style={{fontSize:32, color:'#c04040'}}>⚠</div>
      <div style={{color:'#f07070', fontSize:18, fontWeight:'bold'}}>서버 연결이 끊겼습니다</div>
      <div style={{color:'#8a7a5a', fontSize:13}}>잠시 후 새로고침해 주세요.</div>
      <button style={{
        marginTop:8, padding:'10px 32px', fontSize:14,
        background:'#3a2010', border:'1px solid #c8820a',
        color:'#f0c060', borderRadius:4, cursor:'pointer',
      }} onClick={() => window.location.reload()}>새로고침</button>
    </div>
  )
}

const TABS = [
  { id: 'inven',   label: '행낭' },
  { id: 'mugong',  label: '무공' },
  { id: 'train',   label: '훈련' },
  { id: 'sect',    label: '문파' },
  { id: 'shop',    label: '상점' },
  { id: 'trade',   label: '거래소' },
  { id: 'hunt',    label: '무림행' },
  { id: 'move',    label: '이동' },
  { id: 'rank',    label: '랭킹' },
]

const REGION_NAME: Record<string, string> = {
  RGN_BUKDO:'북도', RGN_BUKHAI:'북해', RGN_DAEMO:'대막', RGN_DALI:'대리국',
  RGN_GAEBEONG:'개봉', RGN_HAEDONG:'해동', RGN_HANGSAN:'항산', RGN_HEBEI:'하북',
  RGN_HONAM:'호남', RGN_HUASHAN:'화산', RGN_HWANGSAN:'황산', RGN_JANGAN:'장안',
  RGN_JANGBACK:'장백산', RGN_JIANGNAN:'강남', RGN_JIANGSU:'강소', RGN_JIRIM:'지림',
  RGN_MINGGU:'광명정', RGN_NAKYANG:'낙양', RGN_NAMMAN:'남만', RGN_NANDO:'남도',
  RGN_SANDO:'산동', RGN_SHAOLIN:'소림사', RGN_SICHUAN:'사천', RGN_TIBET:'서장',
  RGN_TOEI:'동영', RGN_WUDANG:'무당산', RGN_XIYUE:'서역', RGN_ZHONGYUAN:'중원',
  RGN_CHEONWOECHEON:'천외천',
}

function parseTurnSec(msg: string): number {
  const m = msg?.match(/sec:(\d+)/)
  return m ? parseInt(m[1], 10) : 60
}

function parseCharLoad(resultData: string, userCode: number, userName: string): GameState {
  const raw = JSON.parse(resultData || '{}')
  const state = initGameState(userCode, userName)

  const base = raw.charBase ?? raw  // 구버전 호환
  if (base.BODY_TYPE)        state.char.bodyType      = base.BODY_TYPE
  if (base.BODY_STATUS)      state.char.bodyStatus    = base.BODY_STATUS
  if (base.MUGONG_STAGE)     state.char.mugongStage   = base.MUGONG_STAGE
  if (base.HP  != null)        state.char.hp          = Number(base.HP)
  if (base.MAX_HP != null)     state.char.maxHp       = Number(base.MAX_HP)
  if (base.ATK != null)      state.char.atk           = Number(base.ATK)
  if (base.DEF != null)      state.char.def           = Number(base.DEF)
  if (base.SPEED != null)    state.char.speed         = Number(base.SPEED)
  if (base.GOLD != null)     state.char.gold          = Number(base.GOLD)
  if (base.CURRENT_REGION)   state.char.currentRegion = base.CURRENT_REGION
  if (base.TOTAL_POWER != null) state.char.totalPower = Number(base.TOTAL_POWER)
  if (base.TURN_COUNT != null)  state.char.turnCount  = Number(base.TURN_COUNT)
  if (base.LAST_GLOBAL_TURN != null) state.char.lastGlobalTurn = Number(base.LAST_GLOBAL_TURN)
  if (base.ACTIVE_ACTION)    state.char.activeAction  = base.ACTIVE_ACTION
  if (base.ACTIVE_TARGET)    state.char.activeTarget  = base.ACTIVE_TARGET
  if (base.ACTIVE_REGION)    state.char.activeRegion  = base.ACTIVE_REGION
  if (base.LAST_ACTION_AT)   state.char.lastActionAt  = base.LAST_ACTION_AT
  state.char.sectId   = base.SECT_ID   != null ? Number(base.SECT_ID)   : null
  state.char.sectRole = base.SECT_ROLE ?? null

  if (raw.elements)   state.elements   = raw.elements as Partial<Record<Element, number>>
  if (raw.categories) state.categories = raw.categories as Record<string, number>

  if (raw.inventory) state.inventory = (raw.inventory as any[]).map(r => ({
    invId:      Number(r.INV_ID),
    itemId:     String(r.ITEM_ID),
    name:       String(r.ITEM_NAME),
    category:   r.CATEGORY,
    slot:       r.SLOT ?? undefined,
    grade:      r.GRADE,
    atkBonus:   Number(r.ATK_BONUS)   || 0,
    defBonus:   Number(r.DEF_BONUS)   || 0,
    hpBonus:    Number(r.HP_BONUS)    || 0,
    speedBonus: Number(r.SPEED_BONUS) || 0,
    mugongId:   r.MUGONG_ID ?? undefined,
    sellPrice:  Number(r.SELL_PRICE) || 0,
    qty:        Number(r.QTY) || 1,
    equipSlot:   r.EQUIP_SLOT ?? undefined,
    enhanceLevel: Number(r.ENHANCE_LEVEL) || 0,
    consumeType: r.CONSUME_TYPE || undefined,
  }))

  if (raw.mugongs) state.mugongs = (raw.mugongs as any[]).map(r => ({
    mugongId:  String(r.MUGONG_ID),
    name:      String(r.MUGONG_NAME),
    category:  String(r.CATEGORY),
    element:   r.ELEMENT ?? undefined,
    grade:     r.GRADE,
    maxStar:   Number(r.MAX_STAR) || 10,
    star:      Number(r.STAR) || 1,
    exp:       Number(r.EXP)  || 0,
    activeYn:     Boolean(r.ACTIVE_YN),
    equippedSlot: r.EQUIPPED_SLOT ?? undefined,
    sectOnly:     r.SECT_ONLY ?? undefined,
    atkBonus:  Number(r.ATK_BONUS_PER_STAR) || 0,
    defBonus:  Number(r.DEF_BONUS_PER_STAR) || 0,
    hpBonus:   Number(r.HP_BONUS_PER_STAR)  || 0,
    spdBonus:  Number(r.SPD_BONUS_PER_STAR) || 0,
  }))

  return state
}

export interface HuntTickResult {
  turnsDone:    number
  goldDelta:    number
  dropItemId:   string | null
  dropItemName: string | null
  dropQty:      number
  turn:         number
  monsterName:  string | null
  hpDamage:     number
  hpRecover:    number
  autoHpUsed:   number
}

export default function App() {
  const [session,        setSession]        = useState<Session | null>(loadSession)
  const [gs,             setGs]             = useState<GameState | null>(null)
  const [tab,            setTab]            = useState('inven')
  const [loading,        setLoading]        = useState(false)
  const [serverDown,     setServerDown]     = useState(false)
  const [lastHuntResult, setLastHuntResult] = useState<HuntTickResult | null>(null)

  // 턴 타이머
  const [turnSec,    setTurnSec]    = useState(60)
  const [globalTurn, setGlobalTurn] = useState(0)
  const syncedRef        = useRef(false)
  const prevGlobalTurn   = useRef(0)
  const tickInProgress   = useRef(false)
  const activeActionRef  = useRef<string | null>(null)

  function markServerDown(err: unknown) {
    if (axios.isAxiosError(err) && !err.response) setServerDown(true)
  }

  // 서버 턴 상태 동기화
  async function syncTurnStatus(token: string) {
    try {
      const res = await apiTurnStatus(token)
      if (res.resultCode === '00') {
        const sec   = parseTurnSec(res.resultMessage)
        const parts = res.resultData?.split('\x18') ?? []
        const gTurn = Number(parts[2]) || 0
        setTurnSec(sec)
        setGlobalTurn(gTurn)
      }
    } catch {}
  }

  // gs 변경 시 activeAction ref 동기화
  useEffect(() => {
    activeActionRef.current = gs?.char.activeAction ?? null
  }, [gs])

  // 세션 만료 이벤트 수신 → 로그인 화면으로
  useEffect(() => {
    const onExpired = () => { clearSession(); setSession(null); setGs(null) }
    window.addEventListener('session-expired', onExpired)
    return () => window.removeEventListener('session-expired', onExpired)
  }, [])

  // 새로고침 시 세션 유지: session 있고 gs 없으면 캐릭터 자동 로드
  useEffect(() => {
    if (!session || gs) return
    setLoading(true)
    apiCharLoad(session.token)
      .then(res => {
        if (res.resultCode === '00') {
          setGs(parseCharLoad(res.resultData ?? '{}', session.userCode, session.userName))
        } else {
          // 서버에서 세션 거부 → 로그인 화면
          clearSession(); setSession(null)
        }
      })
      .catch(() => { clearSession(); setSession(null) })
      .finally(() => setLoading(false))
  }, [session]) // gs 의존성 제외: 최초 1회(gs=null)에만 실행됨

  // 로그인 후 & 탭 포커스 시 턴 동기화
  useEffect(() => {
    if (!session) return
    syncTurnStatus(session.token)
    syncedRef.current = true

    const onFocus = () => syncTurnStatus(session.token)
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [session])

  // 1초 카운트다운 + 턴 틱 처리
  useEffect(() => {
    if (!session) return
    const timer = setInterval(() => {
      setTurnSec(prev => {
        if (prev <= 1) {
          setGlobalTurn(g => {
            const newTurn = g + 1
            // 활성 행동이 있을 때만 ACTION_TICK 호출
            if (activeActionRef.current && !tickInProgress.current) {
              tickInProgress.current = true
              apiActionTick(session.token, newTurn)
                .then(res => {
                  if (res.resultCode === '00' && res.resultData) {
                    const reward = JSON.parse(res.resultData)
                    if (Number(reward.TURNS_DONE) > 0) {
                      refreshGs()
                      setLastHuntResult({
                        turnsDone:   Number(reward.TURNS_DONE),
                        goldDelta:   Number(reward.GOLD_DELTA),
                        dropItemId:   reward.DROP_ITEM_ID   || null,
                        dropItemName: reward.DROP_ITEM_NAME || null,
                        dropQty:      Number(reward.DROP_QTY)    || 0,
                        turn:         newTurn,
                        monsterName:  reward.MONSTER_NAME   || null,
                        hpDamage:   Number(reward.HP_DAMAGE)   || 0,
                        hpRecover:  Number(reward.HP_RECOVER)  || 0,
                        autoHpUsed: Number(reward.AUTO_HP_USED)|| 0,
                      })
                    }
                  }
                })
                .catch(() => {})
                .finally(() => { tickInProgress.current = false })
            }
            prevGlobalTurn.current = newTurn
            return newTurn
          })
          syncTurnStatus(session.token)
          return 60
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [session])

  async function handleLogin(token: string, userCode: number, userName: string) {
    const sess: Session = { token, userCode, userName }
    saveSession(sess)
    setSession(sess)
    setLoading(true)
    try {
      const res = await apiCharLoad(token)
      if (res.resultCode === '00') {
        setGs(parseCharLoad(res.resultData ?? '{}', userCode, userName))
      }
    } catch (e) { markServerDown(e) }
    finally { setLoading(false) }
  }

  async function refreshGs() {
    if (!session) return
    try {
      const res = await apiCharLoad(session.token)
      if (res.resultCode === '00') setGs(parseCharLoad(res.resultData ?? '{}', session.userCode, session.userName))
    } catch {}
  }

  async function handleLogout() {
    if (session) {
      try { await apiUserLogout(session.token) } catch {}
    }
    clearSession(); setSession(null); setGs(null)
  }

  if (serverDown) return <ServerDownOverlay />
  if (!session)   return <LoginPage onLogin={handleLogin} />

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', color:'#f0d080', fontSize:18 }}>
      강호로 나가는 중...
    </div>
  )

  const char = gs?.char
  const turnPct = Math.round((turnSec / 60) * 100)
  const turnColor = turnSec <= 10 ? '#e05030' : turnSec <= 20 ? '#c8820a' : '#507050'

  return (
    <div className="app-container">
      {/* 상단 정보바 */}
      <div className="top-bar">
        <span className="char-name">{char?.userName ?? session.userName}</span>
        <span className="char-stage">{char?.mugongStage ?? '三流'}</span>
        <span className="char-gold">{(char?.gold ?? 0).toLocaleString()} 전</span>
        <button className="logout-btn" onClick={handleLogout}>퇴장</button>
      </div>

      {/* 능력치바 */}
      <div className="stat-bar">
        <span style={{ color:'#e05050' }}>체력 {char?.hp ?? 0}/{char?.maxHp ?? 100}</span>
        <span>공 {char?.atk ?? 0}</span>
        <span>방 {char?.def ?? 0}</span>
        <span style={{ color:'#e0c060' }}>전투력 {(char?.totalPower ?? 0).toLocaleString()}</span>
        <span style={{ marginLeft:'auto', color:'#a09070' }}>{REGION_NAME[char?.currentRegion ?? ''] ?? char?.currentRegion ?? '하남'}</span>
      </div>

      {/* 턴 타이머 — 행동 중일 때만 표시 */}
      {char?.activeAction ? (
        <div className="turn-bar">
          <span className="turn-label">남은시간</span>
          <div className="turn-progress-bg">
            <div className="turn-progress-fill" style={{ width:`${turnPct}%`, background: turnColor }} />
          </div>
          <span className="turn-sec" style={{ color: turnColor }}>{turnSec}초</span>
          <span className="turn-action">
            {char.activeAction === 'TRAIN' ? '수련 중' : char.activeAction === 'RECOVER' ? '운기행공 중' : '사냥 중'}
          </span>
        </div>
      ) : (
        <div className="turn-bar" style={{ justifyContent:'center' }}>
          <span style={{ fontSize:11, color:'#504030' }}>대기 중 — 수련 또는 사냥을 시작하세요</span>
        </div>
      )}

      {/* 탭 메뉴 */}
      <div className="tab-nav">
        {TABS.map(t => (
          <button key={t.id}
            className={`tab-btn${tab===t.id?' active':''}`}
            onClick={() => setTab(t.id)}
          >{t.label}</button>
        ))}
      </div>

      {/* 탭 컨텐츠 */}
      <div className="tab-content">
        {tab === 'inven'  && gs && session && <TabInven  gs={gs} token={session.token} onRefresh={refreshGs} />}
        {tab === 'mugong' && gs && session && <TabMugong gs={gs} token={session.token} onRefresh={refreshGs} />}
        {tab === 'train'  && gs && session && <TabTrain  gs={gs} token={session.token} onRefresh={refreshGs} onForceLogout={handleLogout} />}
        {tab === 'hunt'   && gs && session && <TabHunt   gs={gs} token={session.token} onRefresh={refreshGs} onForceLogout={handleLogout} lastHuntResult={lastHuntResult} />}
        {tab === 'shop'   && gs && session && <TabShop   gs={gs} token={session.token} onRefresh={refreshGs} />}
        {tab === 'trade'  && gs && session && <TabTrade  gs={gs} token={session.token} onRefresh={refreshGs} />}
        {tab === 'move'   && gs && session && <TabMove   gs={gs} token={session.token} onRefresh={refreshGs} />}
        {tab === 'sect'   && gs && session && <TabSect   gs={gs} token={session.token} onRefresh={refreshGs} />}
        {tab === 'rank'   && <TabRank />}
        {!['inven','mugong','train','hunt','shop','trade','move','sect','rank'].includes(tab) && (
          <div style={{ color:'#8a7a5a', textAlign:'center', paddingTop:60, fontSize:14 }}>
            [{TABS.find(t=>t.id===tab)?.label}] — 구현 예정
          </div>
        )}
      </div>
    </div>
  )
}
